import { Component, Element, Prop, VNode, Watch, h } from '@stencil/core';

import * as Pipeline from '../core/utils/pipeline';
import { BaseComponent } from '../core/interfaces/BaseComponent';
import { Theme } from '../core/types/globalTypes';
import { generateId } from '../core/utils/components';
import { isNullOrUndefinedOrEmpty } from 'utils/collection';

/**
 * Tags are used for items that need to be labeled, categorized, or organized using keywords that describe them. Multiple or single tags can be used to categorize items.
 * @tag `<ads-tag>`
 * @slot - Content rendered as rich text inside the component
 * @example <ads-tag text="Hello world"></ads-tag>
 */
@Component({
  tag: 'ads-tag',
  styleUrl: 'ads-tag.scss',
  shadow: true,
})
export class AdsTag implements BaseComponent {
  /**
   * @hidden
   */
  public component: string;

  constructor() {
    this.el.setAttribute('id', this.identifier || generateId('ads-tag'));
    this.component = 'ads-tag';
  }

  @Element() private el: HTMLAdsTagElement;

  /**
   * The unique identifier (optional)
   */
  @Prop({ attribute: 'id' }) identifier?: string;

  /**
   * The theme for this component instance (optional)
   * Values: "default", "dark"
   */
  @Prop({ reflect: true }) theme?: Theme;

  /**
   * Contains a URL (fragment).
   * If this property is set, an anchor tag will be rendered. (optional)
   */
  @Prop({ reflect: true }) href?: string;

  /**
   * Text to display as the aria-label (optional)
   */
  @Prop({ reflect: true }) srLabel?: string;

  /**
   * Specifies whether the tag is clickable or not (optional)
   * @default false
   */
  @Prop({ reflect: true }) isClickable = false;

  /**
   * Specifies an initial value for the tag (optional)
   */
  @Prop({ reflect: true }) text?: string;

  /**
   * Handle identifier property changes
   * @param {string} newValue The new value for "identifier"
   * @hidden
   */
  @Watch('identifier')
  handleIdentifierChange(newValue: string): void {
    this.el.setAttribute('id', newValue || generateId('ads-tag'));
  }

  protected componentDidLoad(): void {
    Pipeline.addComponent(this.el);
  }

  protected disconnectedCallback(): void {
    Pipeline.removeComponent(this.el);
  }

  protected render(): VNode {
    const TagType = !isNullOrUndefinedOrEmpty(this.href) ? 'a' : 'button';
    let attributes = TagType === 'a' ? { href: this.href, tabindex: 0 } : {};
    attributes = !isNullOrUndefinedOrEmpty(this.srLabel) ? { ...attributes, ...{ 'aria-label': this.srLabel } } : attributes;

    return (
      <TagType {...attributes} class={`c-tag ${!isNullOrUndefinedOrEmpty(this.href) || this.isClickable ? 'is-clickable' : ''}`}>
        {!isNullOrUndefinedOrEmpty(this.text) ? this.text : <slot />}
      </TagType>
    ) as VNode;
  }
}
